import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Activity, RefreshCw, CheckCircle2, XCircle, Cpu, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ToolHealth() {
  const { user } = useAuth();
  const [data, setData] = useState({ tools: [], ready: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(false);

  const load = () => {
    setLoading(true);
    api.toolsHealth().then(setData).catch(() => toast.error("Impossible de récupérer l'état des outils")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const reinstall = async () => {
    setInstalling(true);
    try {
      const r = await api.installTools();
      toast.success(r.message || "Installation lancée");
      setTimeout(load, 8000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du lancement");
    } finally { setInstalling(false); }
  };

  const allReady = data.total > 0 && data.ready === data.total;
  const isAdmin = user?.role === "admin";

  return (
    <div className="px-4 md:px-8 py-8 max-w-[1100px] mx-auto space-y-6 animate-fade-in" data-testid="tools-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-primary tracking-widest mb-2">// SANTÉ DES OUTILS</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><Activity className="h-8 w-8 text-primary" /> Santé des outils</h1>
          <p className="text-muted-foreground mt-1 text-sm">État des scanners détectés sur le serveur de production en temps réel.</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button onClick={reinstall} disabled={installing} className="font-mono text-xs gap-2 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="tools-reinstall-btn">
              <Download className={cn("h-3.5 w-3.5", installing && "animate-pulse")} /> {installing ? "LANCEMENT…" : "RÉINSTALLER LES OUTILS"}
            </Button>
          )}
          <Button variant="outline" onClick={load} className="font-mono text-xs gap-2" data-testid="tools-refresh-btn">
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /> ACTUALISER
          </Button>
        </div>
      </div>

      <Card className={cn("p-5 flex items-center gap-4 border", allReady ? "border-success/40 bg-success/5" : "border-warning/40 bg-warning/5")} data-testid="tools-summary">
        <div className={cn("h-12 w-12 rounded-md border flex items-center justify-center shrink-0", allReady ? "border-success/40 bg-success/10" : "border-warning/40 bg-warning/10")}>
          <Cpu className={cn("h-6 w-6", allReady ? "text-success" : "text-warning")} />
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-0.5">// MOTEURS</div>
          <div className="font-bold text-lg">
            <span className={allReady ? "text-success" : "text-warning"} data-testid="tools-ready-count">{data.ready}</span>
            <span className="text-muted-foreground"> / {data.total} scanners prêts</span>
          </div>
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.tools.map((t) => (
          <Card key={t.id} className={cn("p-5 border transition-colors", t.available ? "border-border bg-card/60" : "border-destructive/40 bg-destructive/5")} data-testid={`tool-card-${t.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-base font-bold">{t.name}</span>
              {t.available
                ? <CheckCircle2 className="h-5 w-5 text-success" data-testid={`tool-${t.id}-ok`} />
                : <XCircle className="h-5 w-5 text-destructive" data-testid={`tool-${t.id}-down`} />}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed min-h-[32px]">{t.desc}</p>
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
              <Badge variant="outline" className={cn("font-mono text-[9px] uppercase tracking-widest", t.available ? "border-success/40 text-success" : "border-destructive/40 text-destructive")}>
                {t.available ? "ACTIF" : "INDISPONIBLE"}
              </Badge>
              <span className="font-mono text-[9px] text-muted-foreground uppercase tracking-widest">{t.source}</span>
            </div>
          </Card>
        ))}
      </div>

      <p className="font-mono text-[11px] text-muted-foreground">
        // Les outils « démarrage » (nikto, searchsploit) s'installent au lancement du serveur ; s'ils sont indisponibles, le scanner les ignore proprement.
      </p>
    </div>
  );
}
